// @ts-nocheck
/**
 * OOTW cart-drawer boost.
 *
 * Opens the cart drawer on ATC and cart-icon clicks, manages the skeleton
 * overlay, keeps the header badge in sync, and guards against scroll-lock freeze.
 *
 * ── Empty-cart-after-ATC fix (the hard one) ───────────────────────────────
 * Our ootw-subscribe-save selector writes `selling_plan` into the product form,
 * so Horizon's product-form.js adds the subscription in ONE /cart/add.js call
 * and the native morph shows the item correctly at first. BUT Skio's app embed
 * then asynchronously re-processes the cart (it empties / re-renders the line a
 * second or two later), which leaves the drawer stuck on its empty state.
 *
 * A single delayed check can't catch this — the drawer looks fine at 600ms and
 * only goes empty afterwards. So we run a WATCHDOG for ~8s after every ATC: on
 * each tick, if the drawer shows empty while /cart.js actually has items, we
 * re-render the section via the Section Rendering API (`?section_id=…`, the form
 * that returns the full #shopify-section-… wrapper morphSection needs) and
 * dispatch cart:update so Horizon's native morph path repaints it — correct
 * layout, dialog stays open. Once the cart settles with items, the watchdog
 * idles until its window expires.
 * ──────────────────────────────────────────────────────────────────────────
 */
(function () {
  var LOADING_ATTR = 'data-ootw-loading';
  var SECTION_ID = 'ootw-cart-drawer';
  var SAFETY_MS = 8000;
  var WATCHDOG_MS = 8000;     // how long to watch after an ATC
  var TICK_MS = 600;          // watchdog poll interval
  var loadingTimer = null;
  var atcPending = false;
  var watchdogTimer = null;
  var watchdogUntil = 0;

  function getDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  function clearLoading() {
    clearTimeout(loadingTimer);
    loadingTimer = null;
    var d = getDrawer();
    if (d) d.removeAttribute(LOADING_ATTR);
  }

  function unfreezeIfStuck() {
    if (document.body.style.position !== 'fixed') return;
    if (document.querySelector('dialog[open]')) return;
    var top = document.body.style.top || '0';
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    var y = Math.abs(parseInt(top, 10)) || 0;
    window.scrollTo({ top: y, behavior: 'instant' });
  }

  function openDrawer(d) {
    if (!d) return;
    var dlg = d.querySelector('dialog');
    if (dlg && dlg.open) return;

    if (typeof d.open === 'function') { d.open(); return; }

    if (!dlg) return;
    var sy = window.scrollY || window.pageYOffset || 0;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + sy + 'px';
    dlg.showModal();
    dlg.addEventListener('close', function () {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: sy, behavior: 'instant' });
    }, { once: true });
  }

  function updateHeaderCartCount(count) {
    if (typeof count !== 'number' || isNaN(count)) return;
    ['cart-icon-bubble', 'cart-icon-bubble-mobile'].forEach(function (id) {
      var icon = document.getElementById(id);
      if (!icon) return;
      var badge = icon.querySelector('.custom-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'custom-header__cart-count';
          icon.appendChild(badge);
        }
        badge.innerHTML = '<span aria-hidden="true">' + (count < 100 ? count : '') + '</span>';
      } else if (badge && badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }
    });
  }

  /** True when the drawer is showing empty-cart state (no .cart-form visible). */
  function drawerShowsEmpty() {
    return !document.querySelector(
      'cart-items-component[data-section-id="' + SECTION_ID + '"] .cart-form'
    );
  }

  /**
   * Fetch the section via the Section Rendering API (singular `section_id`, raw
   * HTML WITH the #shopify-section-… wrapper) and dispatch cart:update so
   * Horizon's cart-items-component morphs it in. Resolves true if it dispatched.
   */
  function applyFreshSection(itemCount) {
    return fetch('/?section_id=' + SECTION_ID, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!html) return false;
        // Only apply if the rendered section truly has cart items (avoids
        // repainting a transient empty render while Skio is mid-process).
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('.cart-form')) return false;

        var sectionMap = {};
        sectionMap[SECTION_ID] = html;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            _ootw_refresh: true,                 // guard: don't re-arm watchdog
            resource: { item_count: itemCount },
            data: { sections: sectionMap }
          }
        }));
        updateHeaderCartCount(itemCount);
        return true;
      })
      .catch(function () { return false; });
  }

  /** (Re)start the watchdog window. Safe to call repeatedly. */
  function armWatchdog() {
    watchdogUntil = Date.now() + WATCHDOG_MS;
    if (!watchdogTimer) tickWatchdog();
  }

  function tickWatchdog() {
    watchdogTimer = null;
    if (Date.now() > watchdogUntil) return;

    // Drawer currently looks fine — nothing to repair, just keep watching in
    // case Skio empties it shortly.
    if (!drawerShowsEmpty()) {
      watchdogTimer = setTimeout(tickWatchdog, TICK_MS);
      return;
    }

    // Drawer is empty — only repair if the cart genuinely has items.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && cart.item_count > 0) {
          return applyFreshSection(cart.item_count);
        }
        return false;
      })
      .catch(function () { return false; })
      .then(function () {
        watchdogTimer = setTimeout(tickWatchdog, TICK_MS);
      });
  }

  // ── 1. Add-to-cart click → open drawer + skeleton + arm watchdog ─────────

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    var btn = target.closest('button[name="add"]');
    if (!btn || btn.disabled) return;
    var form = btn.closest('form');
    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;
    var d = getDrawer();
    if (!d) return;
    atcPending = true;
    d.setAttribute(LOADING_ATTR, '');
    clearTimeout(loadingTimer);
    loadingTimer = setTimeout(function () { atcPending = false; clearLoading(); }, SAFETY_MS);
    openDrawer(d);
    armWatchdog(); // start watching now; covers the whole Skio settling window
  }, { capture: true });

  // ── 2. cart:update ───────────────────────────────────────────────────────

  document.addEventListener('cart:update', function (e) {
    var detail = e.detail || {};
    var isError = !!(detail.data && detail.data.didError);

    var resource = detail.resource;
    if (resource && typeof resource.item_count === 'number') {
      updateHeaderCartCount(resource.item_count);
    } else if (!isError) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { updateHeaderCartCount(c.item_count); })
        .catch(function () {});
    }

    if (isError) { atcPending = false; clearLoading(); return; }

    if (atcPending) {
      atcPending = false;
      var d = getDrawer();
      if (d) openDrawer(d);
    }

    requestAnimationFrame(function () { requestAnimationFrame(clearLoading); });

    // Any real cart update (not our own refresh) re-arms the watchdog so a late
    // Skio re-render is always caught.
    if (!detail._ootw_refresh) armWatchdog();

    setTimeout(unfreezeIfStuck, 250);
    setTimeout(unfreezeIfStuck, 900);
  });

  document.addEventListener('cart:error', function () {
    atcPending = false;
    clearLoading();
    setTimeout(unfreezeIfStuck, 250);
  });

  document.addEventListener('dialog:close', function () {
    setTimeout(unfreezeIfStuck, 50);
  });

  // ── 3. Cart-icon click → open drawer ─────────────────────────────────────

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    var trigger = target.closest('[data-custom-cart-trigger], [data-testid="cart-drawer-trigger"]');
    if (!trigger) return;
    var d = getDrawer();
    if (!d) return;
    e.preventDefault();
    openDrawer(d);
  }, { capture: true });

  // ── 4. Minus button at qty=1 → remove the item from cart ─────────────────
  // Horizon's quantity-selector has min="1" so the normal minus click is a
  // no-op when the value is already 1. We intercept it first (capture phase),
  // find the line-item key from the parent <tr data-key>, and call
  // /cart/change.js with quantity:0 to remove the item.

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    // Must be inside our cart drawer
    var drawer = document.querySelector('.ootw-cart-drawer');
    if (!drawer || !drawer.contains(target)) return;

    // Find the clicked button — must be inside a quantity-selector
    var btn = target.closest('button');
    if (!btn) return;
    var qtySelector = btn.closest('quantity-selector');
    if (!qtySelector) return;

    // Confirm it's the minus / decrease button
    var name = btn.getAttribute('name') || '';
    var label = (btn.getAttribute('aria-label') || '').toLowerCase();
    var text = btn.textContent.trim();
    var isDecrease = name === 'minus'
      || label.indexOf('decrease') !== -1
      || label.indexOf('minus') !== -1
      || text === '-' || text === '−';
    // Fallback: first button inside the selector is usually minus
    if (!isDecrease) {
      isDecrease = btn === qtySelector.querySelector('button');
    }
    if (!isDecrease) return;

    // Only act when the current value is exactly 1
    var input = qtySelector.querySelector('input[type="number"], input[name*="quantity"], input[name*="updates"]');
    if (!input) return;
    var currentQty = parseInt(input.value, 10);
    if (currentQty !== 1) return;

    // Find the line-item key on the closest table row
    var row = btn.closest('tr[data-key]');
    if (!row) return;
    var lineKey = row.getAttribute('data-key');
    if (!lineKey) return;

    // Take over from Horizon — prevent it from resetting the input to min=1
    e.stopImmediatePropagation();

    var d = getDrawer();
    if (d) d.setAttribute(LOADING_ATTR, '');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: [SECTION_ID] })
    })
      .then(function (r) { return r.json(); })
      .then(function (cartData) {
        var itemCount = (cartData && typeof cartData.item_count === 'number') ? cartData.item_count : 0;
        updateHeaderCartCount(itemCount);
        var sectionHtml = cartData && cartData.sections && cartData.sections[SECTION_ID];
        if (sectionHtml) {
          var sectionMap = {};
          sectionMap[SECTION_ID] = sectionHtml;
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              _ootw_refresh: true,
              resource: { item_count: itemCount },
              data: { sections: sectionMap }
            }
          }));
        } else {
          applyFreshSection(itemCount);
        }
      })
      .catch(function () { clearLoading(); });
  }, { capture: true });
})();
